/* eslint-disable no-shadow */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';

import ErrorPage from '../../components/ErrorPage';

import { fetchComponentConfig } from '../../actions/component-config-actions';
import * as eventActionsV1 from '../../actions/event-actions';
import * as eventActionsV2 from '../../actions/event-profile-actions';
import * as headerActions from '../../actions/header-actions';

import config from '../../../config';
import withAuth from '../../components/hocs/withAuth';
import withStatsigEventUpdate from '../../components/hocs/withStatsigEventUpdate';
import useIsMobile from '../../components/hooks/useIsMobile';
import { useStatsig } from '../../components/hooks/useStatsig';
import { SocketNotificationTypes } from '../../components/SocketNotifications';
import { useSocketNotifications } from '../../components/SocketNotifications/useSocketNotifications';
import { getStandardErrorPageConfig } from '../../utils/errorPage';
import { EventTypes, isEventPTT, isEventType } from '../../utils/events';
import AdultTeamTennisProfilePage from './AdultTeamTennisMatchProfilePage';
import CreatePlayProfilePage from './CreatePlayProfilePage';
import DualMatchProfilePage from './DualMatchProfilePage';
import EventProfilePageV3 from './EventProfilePageV3';
import PaidHitProfilePage from './PaidHitProfilePage';

const EventProfileWrapper = (props) => {
    const { match } = props;
    const { checkGate, featureGates } = useStatsig();

    // v1 reducer
    const { currentEvent } = useSelector(({ events }) => {
        const { currentEvent } = events;
        return { currentEvent };
    });
    // v2 reducer
    const { event } = useSelector(({ eventProfile }) => {
        const { event } = eventProfile;
        return { event };
    });
    const auth = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const { isMobileWidth } = useIsMobile();
    const {
        canInitiateConnection,
        initializeSocketConnection,
    } = useSocketNotifications({
        type: SocketNotificationTypes.LIVE_SCORE,
        autoDisconnectOnUnmount: true,
        onNotificationReceived: (data) => {
            dispatch(eventActionsV2.updateLiveScoreData(data));
        },
    });

    const collegeDualMatchFlagEnabled = checkGate(featureGates.COLLEGE_DUAL_MATCH);
    const isLiveScoresEnabled = checkGate(featureGates.WEB_PTT_LIVE_SCORES);
    const isPttEvent = isEventPTT(event?.data);

    const loadEventV1 = async () => {
        await dispatch(eventActionsV1.getEvent(match.params.eventId, false, false, false));
    };
    const loadEventV2 = async () => {
        await dispatch(eventActionsV2.getEvent(match.params.eventId));
    };

    const initState = () => {
        dispatch(eventActionsV1.initState());
        dispatch(eventActionsV2.initState());
    };

    useEffect(() => {
        initState();
        return () => {
            dispatch(headerActions.setHeader({ oldLogo: false }));
        };
    }, []);

    useEffect(() => {
        const requestedEventId = parseInt(match?.params?.eventId);
        const currentEventId = parseInt(event?.data?.id);
        const erroredEventId = event?.error?.eventId ? parseInt(event.error.eventId) : null;

        if (requestedEventId !== currentEventId && !event.loading && requestedEventId !== erroredEventId) {
            loadEventV2();
            dispatch(fetchComponentConfig('event-profile', { eventId: match.params.eventId }));
        }
    }, [match?.params?.eventId, event?.data?.id, event?.loading, event?.error]);

    useEffect(() => {
        // load v1 event for event profiles still using the v1 data model
        if (isEventType(event.data, EventTypes.FREE_PLAY) ||
            isEventType(event.data, EventTypes.DUAL_MATCH) ||
            isEventType(event.data, EventTypes.TEAM_MATCH) ||
            isEventType(event.data, EventTypes.PAID_HIT)) {
            const hasEventData = event?.data?.id;
            const isSameEventData = currentEvent?.data?.id === event?.data?.id;
            if (hasEventData && !isSameEventData && !currentEvent.loading && !event.loading) {
                loadEventV1();
            }
        }
        if (event.data?.club?.id && config.oldLogoClubIds.includes(parseInt(event.data.id))) {
            dispatch(headerActions.setHeader({ oldLogo: true }));
        }
    }, [event?.data?.id, currentEvent?.data?.id]);

    useEffect(() => {
        // v1
        if (currentEvent.data?.club?.id && config.oldLogoClubIds.includes(parseInt(currentEvent.data.club.id))) {
            dispatch(headerActions.setHeader({ oldLogo: true }));
        }
    }, [currentEvent?.data?.id]);

    useEffect(() => {
        const isEventDataLoaded = !event.loading && event.data?.id;
        const isUserLoggedIn = auth?.user?.id;
        const shouldSubscribeToLiveScores = isEventDataLoaded && isUserLoggedIn && isLiveScoresEnabled && isPttEvent;
        if (shouldSubscribeToLiveScores && canInitiateConnection()) {
            initializeSocketConnection();
        }
    }, [auth?.user?.id, event.data?.id, event.loading, isLiveScoresEnabled, isPttEvent, canInitiateConnection, initializeSocketConnection]);

    const getErrorPage = (errorData) => {
        const errorConfig = getStandardErrorPageConfig({ errorData, resourceName: 'event' });
        let onBackHandler;

        if (errorData?.statusCode === 404 && errorData?.redirectUrl) {
            // For 404 errors with redirect URL, use that for the back button
            onBackHandler = () => {
                window.location.href = errorData.redirectUrl;
            };
        }

        return (
            <ErrorPage
                subtitle={errorConfig.subtitle}
                showReloadButton={errorConfig.showReloadButton}
                showBackButton={errorConfig.showBackButton}
                onBack={onBackHandler}
                data-testid="event-profile.error-page"
            />
        );
    };

    if (event.error || currentEvent.error) {
        return getErrorPage(event.error || currentEvent.error);
    }

    const isCollegeDualMatch = currentEvent.data?.club?.tierType?.isCollege;

    if (currentEvent.data?.eventType?.id || event.data?.eventTypeId) {
        switch (currentEvent.data?.eventType?.id || event.data?.eventTypeId) {
            case EventTypes.FREE_PLAY: // 4
                return <CreatePlayProfilePage {...props} />;
            case EventTypes.DUAL_MATCH: // 8
                return isCollegeDualMatch && !collegeDualMatchFlagEnabled
                    ? <Redirect to="/" />
                    : <DualMatchProfilePage isCollegeDualMatch={isCollegeDualMatch} isMobile={isMobileWidth} collegeClubId={currentEvent.data?.club?.id} {...props} />;
            case EventTypes.TEAM_MATCH: // 13
                return <AdultTeamTennisProfilePage {...props} />;
            case EventTypes.PAID_HIT: // 14
                return <PaidHitProfilePage {...props} />;
            default:
                return <EventProfilePageV3 {...props} data-testid="event-profile" />;
        }
    } else {
        return null;
    }
};

const component = withAuth(withStatsigEventUpdate(EventProfileWrapper));

export default {
    component,
    loadData: ({ dispatch }, match) => {
        dispatch(eventActionsV1.getEventMetaData(match.params.eventId));
        dispatch(eventActionsV2.getEventMetaData(match.params.eventId));
    },
};
